import React from "react";
import ReactDOM from "react-dom/client";
import x_button from "src/assets/images/x_button_white.svg";
import toast_success_icon from "src/assets/images/icon/toast-success-icon.svg";
import toast_info_icon from "src/assets/images/icon/toast_info_icon.svg";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";

// 커스텀 토스트 시스템
let toastCount = 0;
const activeToasts: string[] = []; // 활성 토스트 ID 관리

export type ToastType = "success" | "error" | "warning";

interface CustomToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  toastId: string;
}

const CustomToast: React.FC<CustomToastProps> = ({
  message,
  type,
  onClose,
  toastId,
}) => {
  const { t } = useTranslation();
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getToastConfig = () => {
    switch (type) {
      case "success":
        return {
          className: styles.toastSuccess,
          icon: toast_success_icon,
        };
      case "error":
        return {
          className: styles.toastError,
          icon: toast_info_icon,
        };
      case "warning":
        return {
          className: styles.toastWarning,
          icon: toast_info_icon,
        };
      default:
        return {
          className: styles.toastError,
          icon: toast_info_icon,
        };
    }
  };

  const config = getToastConfig();

  // 현재 토스트의 인덱스를 찾아서 위치 계산
  const toastIndex = activeToasts.indexOf(toastId);

  return (
    <div
      className={`${styles.toast} ${config.className}`}
      style={{
        top: `${20 + toastIndex * 65}px`,
        zIndex: 99999 - toastIndex,
      }}
    >
      {/* 아이콘 */}
      <img src={config.icon} alt={type} className={styles.toastIcon} />

      {/* 메시지 */}
      <span className={styles.toastMessage}>{t(message)}</span>

      {/* 닫기 버튼 */}
      <button
        onClick={(e) => {
          console.log("🔥 Custom toast close clicked!");
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
        className={styles.toastCloseButton}
      >
        <img src={x_button} alt="close" className={styles.toastCloseIcon} />
      </button>
    </div>
  );
};

export const showCustomToast = (message: string, type: ToastType) => {
  const toastId = `custom-toast-${++toastCount}`;

  // 활성 토스트 목록에 추가
  activeToasts.push(toastId);

  // 토스트 컨테이너 생성
  const toastElement = document.createElement("div");
  toastElement.id = toastId;
  document.body.appendChild(toastElement);

  const root = ReactDOM.createRoot(toastElement);

  const closeToast = () => {
    console.log("🔥 Closing custom toast:", toastId);

    // 활성 토스트 목록에서 제거
    const index = activeToasts.indexOf(toastId);
    if (index > -1) {
      activeToasts.splice(index, 1);
    }

    // 남은 토스트들의 위치 재조정을 위해 리렌더링
    setTimeout(() => {
      activeToasts.forEach((id, idx) => {
        const element = document.getElementById(id);
        if (element && element.firstChild) {
          const toastDiv = element.firstChild as HTMLElement;
          toastDiv.style.top = `${20 + idx * 65}px`;
          toastDiv.style.zIndex = `${99999 - idx}`;
        }
      });
    }, 50);

    root.unmount();
    if (document.getElementById(toastId)) {
      document.body.removeChild(toastElement);
    }
  };

  root.render(
    <CustomToast
      message={message}
      type={type}
      onClose={closeToast}
      toastId={toastId}
    />
  );

  return toastId;
};

export const notifySuccess = (message: string) => {
  return showCustomToast(message, "success");
};

export const notifyWarning = (message: string) => {
  return showCustomToast(message, "warning");
};

export const notifyError = (message: string) => {
  return showCustomToast(message, "error");
};
